import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Filter, X } from "lucide-react";
import api from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import useSEO from "../hooks/useSEO";

const PROVINCES = [
  "Québec", "Ontario", "Alberta", "Colombie-Britannique", "Manitoba",
  "Saskatchewan", "Nouveau-Brunswick", "Nouvelle-Écosse",
  "Île-du-Prince-Édouard", "Terre-Neuve-et-Labrador",
];

const LANGUAGES = ["Français", "Anglais", "Wolof", "Pulaar", "Arabe", "Espagnol", "Lingala", "Bambara"];

export default function Search() {
  useSEO({ title: "Rechercher un service", description: "Trouvez un professionnel, un entrepreneur ou un organisme francophone par catégorie, province, ville et langue." });
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: params.get("search") || "",
    category: params.get("category") || "",
    province: params.get("province") || "",
    city: params.get("city") || "",
    language: params.get("language") || "",
    profile_type: params.get("profile_type") || "",
    min_rating: params.get("min_rating") || "",
  });
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const q = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
        const { data } = await api.get("/profiles", { params: q });
        setResults(data);
      } finally {
        setLoading(false);
      }
    };
    fetch();
    const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    setParams(cleanFilters, { replace: true });
    // eslint-disable-next-line
  }, [filters]);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const reset = () => setFilters({ search: "", category: "", province: "", city: "", language: "", profile_type: "", min_rating: "" });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="search-page">
      <div className="mb-8">
        <h1 className="font-display font-bold text-4xl sm:text-5xl text-foreground tracking-tight">Je cherche un service</h1>
        <p className="text-muted-foreground mt-2">Filtrez par catégorie, province, langue et plus encore.</p>
      </div>

      {/* Search Bar */}
      <div className="card-surface p-2 mb-6 flex items-center gap-2">
        <SearchIcon className="w-5 h-5 ml-3 text-muted-foreground" />
        <input
          data-testid="search-input"
          type="text"
          value={filters.search}
          onChange={(e) => update("search", e.target.value)}
          placeholder="Mot-clé : ex. plomberie, déménagement, finance…"
          className="flex-1 py-3 outline-none bg-transparent text-sm"
        />
        <button onClick={() => setShowFilters((v) => !v)} className="btn-outline" data-testid="toggle-filters">
          <Filter className="w-4 h-4" /> Filtres
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card-surface p-5 mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="filters-panel">
          <select className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" value={filters.profile_type} onChange={(e) => update("profile_type", e.target.value)} data-testid="filter-type">
            <option value="">Tous types</option>
            <option value="professional">Professionnels</option>
            <option value="entrepreneur">Entrepreneurs</option>
            <option value="organism">Organismes</option>
          </select>
          <select className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" value={filters.category} onChange={(e) => update("category", e.target.value)} data-testid="filter-category">
            <option value="">Toutes catégories</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" value={filters.province} onChange={(e) => update("province", e.target.value)} data-testid="filter-province">
            <option value="">Toutes provinces</option>
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" placeholder="Ville" value={filters.city} onChange={(e) => update("city", e.target.value)} data-testid="filter-city" />
          <select className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" value={filters.language} onChange={(e) => update("language", e.target.value)} data-testid="filter-language">
            <option value="">Toutes langues</option>
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select className="bg-white border border-emerald-900/10 rounded-lg px-3 py-2 text-sm" value={filters.min_rating} onChange={(e) => update("min_rating", e.target.value)} data-testid="filter-rating">
            <option value="">Toute note</option>
            <option value="3">3+ étoiles</option>
            <option value="4">4+ étoiles</option>
            <option value="4.5">4.5+ étoiles</option>
          </select>
          <button onClick={reset} className="col-span-full sm:col-span-1 inline-flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="reset-filters">
            <X className="w-4 h-4" /> Réinitialiser
          </button>
        </div>
      )}

      <div className="mb-4 text-sm text-muted-foreground" data-testid="results-count">{loading ? "Chargement…" : `${results.length} résultat(s)`}</div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {results.map((p) => <ProfileCard key={p.id} profile={p} />)}
      </div>
      {!loading && results.length === 0 && (
        <div className="text-center py-20 text-muted-foreground" data-testid="no-results">
          Aucun résultat. Essayez de modifier vos filtres.
        </div>
      )}
    </div>
  );
}
